/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */

import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ErrorBoundary } from 'react-error-boundary';
import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import { type DocNode } from '@atlaskit/adf-schema';
import { IconButton } from '@atlaskit/button/new';
import { cssMap, cx, jsx } from '@atlaskit/css';
import { INPUT_METHOD } from '@atlaskit/editor-common/analytics';
// eslint-disable-next-line @atlaskit/editor/warn-no-restricted-imports
import { type EditorActions, WithEditorActions } from '@atlaskit/editor-core';
import { ComposableEditor } from '@atlaskit/editor-core/composable-editor';
import { EditorContext } from '@atlaskit/editor-core/editor-context';
import type { JSONDocNode } from '@atlaskit/editor-json-transformer/types';
import { TextSelection } from '@atlaskit/editor-prosemirror/state';
import ArrowUpIcon from '@atlaskit/icon/core/arrow-up';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box, Inline, Stack } from '@atlaskit/primitives/compiled';
import UFOSegment from '@atlaskit/react-ufo/segment';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';
import { token } from '@atlaskit/tokens';
import type { MentionAgentSource } from '@atlassian/conversation-assistant-agent/services/mention-agents/types';
import {
	MAX_CHARACTERS_INPUT,
	useEditorPreset,
} from '@atlassian/conversation-assistant-content-renderer/controllers/editor';
import { useRovoMentionProvider } from '@atlassian/conversation-assistant-content-renderer/utils/mention-provider';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import {
	ExperienceFailure,
	ExperienceStart,
	ExperienceSuccess,
} from '@atlassian/conversation-assistant-instrumentation/ui/ExperienceTracker';
import {
	type Agent,
	type FileUploadResponseItem,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { type AssistanceServiceInputParam } from '@atlassian/conversation-assistant-service/services/assistance-service/main';
import { useEditorPlaceholder } from '@atlassian/conversation-assistant-ui-components/controllers/useEditorPlaceholder';
import { useHighlightActionsEditorPlaceholder } from '@atlassian/conversation-assistant-ui-components/controllers/useHighlightActionsEditorPlaceholder';
import { AtlassianRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';

import { useConversationFiles } from '../../../../common/controllers/conversation-files';
import { useChatMentionResource } from '../../../../common/hooks/useChatMentionResource';
import type { SelectedAgentRef } from '../../../../common/services/rovo-chat-mention-resource/rovo-chat-mention-resource';
import { ChatInputActionButtons } from '../../../../common/ui/chat-input-action-buttons';
import { ComposableEditorWithSkillsGated as ComposableEditorWithSkills } from '../../../../common/ui/composable-editor/ComposableEditorWithSkillsGated';
import { InputContextListDisplay } from '../../../../common/ui/input-context-list-display';
import { type RovoApp } from '../../../../controllers/chat-mode-store/types';
import { useChatModeStoreActions } from '../../../../controllers/chat-mode-store/useChatModeStoreActions';
import { useSkillTagExtensionProvider } from '../../../skills-editor-extension/useSkillTagExtensionProvider';
import { AgentSelector } from '../../agent-selector/AgentSelector';
import type { PromptContentChangePayload } from '../../prompt-content-change-payload';

import { LabsLozenge, type LabsLozengeVariant } from './labs-lozenge';
import messages from './messages';
import { useInlineEditorProps } from './use-inline-editor-props';
import { hasRovoSkillInDoc } from './utils';

export type ArrowKeyPluginConfigMessage = {
	id: string;
	content: string | DocNode;
};

type PromptViewInternalProps = {
	productKey: string;
	siteId: string;
	orgId?: string;
	source: string;
	onSubmit: (params: {
		prompt: DocNode;
		deepResearchEnabled: boolean;
		thinkDeeperEnabled: boolean;
		fastModeEnabled: boolean;
		webSearchEnabled: boolean;
		appFilters?: RovoApp[];
		files?: FileUploadResponseItem[];
		addCurrentPageAsContextUserPreference?: boolean;
	}) => void;
	actions: EditorActions;
	/** `null` clears the selection and returns the prompt to default Rovo. */
	onAgentSelect: (agent: Agent | null) => void;
	isBrowseAgentsModalOpen: boolean;
	setIsBrowseAgentsModalOpen: (isBrowseAgentsModalOpen: boolean) => void;
	enableAgents?: boolean;
	/**
	 * The account ID of the current user. Required when `enableAgents` is true
	 * to power the @agent mention typeahead.
	 */
	currentUserId?: string;
	/**
	 * Agent source from `useMentionAgentSource()`. Provided by the lazy
	 * `PromptViewWithRelay` wrapper
	 */
	agentSource?: MentionAgentSource | null;
	/**
	 * Callback invoked when a user selects an agent from the @mention typeahead.
	 * Provided by the lazy `PromptViewWithRelay` wrapper.
	 */
	onAgentSelected?: (agent: SelectedAgentRef) => void;
	showMauiMediaPlaceholder?: boolean;
	/**
	 * Temporary prop for labs tag use with editor only
	 * @private
	 * @deprecated
	 */
	showLabsLozenge?: boolean;
	labsLozengeVariant?: LabsLozengeVariant;
	agent?: Agent | null;
	popupsMountPoint?: HTMLElement | null;
	useShortMessages?: boolean;
	compact?: boolean;
	onPromptContentChange?: (payload: PromptContentChangePayload) => void;
	defaultValue?: JSONDocNode;
	/**
	 * When `frameless`, the chat-input action buttons (attachments, add, mention,
	 * etc.) and the submit button are hidden — used when the prompt is embedded
	 * inside another toolbar/surface that supplies its own controls.
	 *
	 * Pass `showTrailingActions` to re-show those trailing controls while keeping
	 * the frameless surface.
	 */
	appearance?: 'default' | 'frameless';
	/**
	 * Forces the trailing chat-input action buttons and submit button to render
	 * even when `appearance` is `frameless`. Lets an embedding surface reveal the
	 * add + submit controls on demand (e.g. once the user starts composing a
	 * prompt) without restoring the rest of the framed chrome. No effect when
	 * `appearance` is `default` (the actions already render).
	 */
	showTrailingActions?: boolean;
	shouldRenderInPortal?: boolean;
	/**
	 * Renders a standalone agent selector to the left of the prompt input and
	 * removes the picker from the `+` action-button popup.
	 *
	 * Resolved by `RovoInlineChatPrompt` from its public `agentSelectorLayout`
	 * prop, so the layout decision is made in exactly one place.
	 */
	showInlineAgentSelector?: boolean;
	/**
	 * Optional placeholder override. Takes precedence over the locally-computed
	 * placeholder text before being passed to `useEditorPreset`.
	 */
	placeholderText?: string;
};

export type ChatPromptInputProps = Omit<PromptViewInternalProps, 'actions'>;
const STORE_KEY = 'inline_prompt_view';
const styles = cssMap({
	stack: {
		width: '100%',
		marginBottom: token('space.negative.150'),
	},

	stackWithoutContexts: {
		display: 'contents',
	},

	editorWrapper: {
		maxWidth: '100%',
		display: 'flex',
		alignItems: 'center',
		// Vertically center the editor when on a single line
		paddingTop: token('space.050'),
		marginBottom: token('space.negative.050'),
		// Prevent the actions from growing when inline with the editor
		flexGrow: 99999,
	},

	// Remove padding from the editor content container in compact mode
	editorWrapperCompact: {
		// @ts-expect-error - nested .ak-editor-content-area selector not supported in cssMap strict types; required by ProseMirror editor styling
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'.ak-editor-content-area': {
			paddingTop: token('space.0'),
			paddingRight: token('space.0'),
			paddingBottom: token('space.0'),
			paddingLeft: token('space.0'),
		},
	},

	actions: {
		justifyContent: 'end',
		gap: token('space.100'),
		// Expand to full width when not on the same row as the editor
		flexGrow: 1,
	},

	labsLozenge: {
		alignSelf: 'center',
	},
});

const PromptViewInternal = ({
	productKey,
	siteId,
	orgId,
	source,
	onSubmit,
	actions,
	onAgentSelect,
	isBrowseAgentsModalOpen,
	setIsBrowseAgentsModalOpen,
	enableAgents = false,
	currentUserId,
	agentSource,
	onAgentSelected,
	showLabsLozenge = false,
	labsLozengeVariant = 'default',
	showMauiMediaPlaceholder = false,
	agent,
	popupsMountPoint,
	useShortMessages = false,
	compact = false,
	onPromptContentChange,
	defaultValue,
	appearance = 'default',
	showTrailingActions = false,
	shouldRenderInPortal,
	showInlineAgentSelector = false,
	placeholderText: placeholderTextOverride,
}: PromptViewInternalProps) => {
	di(
		ComposableEditor,
		useEditorPlaceholder,
		useEditorPreset,
		useChatModeStoreActions,
		AtlassianRelayEnvironmentProvider,
	);

	const saveRef = useRef<() => void>();
	const [inputValue, setInputValue] = useState('');
	const [hasMentionOrInlineCard, setHasMentionOrInlineCard] = useState(false);

	const defaultHasSkill = useMemo(() => hasRovoSkillInDoc(defaultValue), [defaultValue]);
	const [hasSkill, setHasSkill] = useState(defaultHasSkill);
	useEffect(() => {
		if (defaultHasSkill) {
			setHasSkill(true);
		}
	}, [defaultHasSkill]);
	const submitButtonRef = useRef<HTMLButtonElement>(null);
	const { value: mentionResourceOverride } = useRovoMentionProvider();
	const { placeholder, clearPlaceholder } = useEditorPlaceholder();
	const { highlightActionsEditorPlaceholder, clearHighlightActionsEditorPlaceholder } =
		useHighlightActionsEditorPlaceholder();
	const { formatMessage } = useIntl();
	const placeholderText =
		placeholderTextOverride ??
		(showMauiMediaPlaceholder ? formatMessage(messages.chatInputAssistiveLabel) : undefined);

	const assistanceServiceParams: AssistanceServiceInputParam['assistanceServiceParams'] = {
		siteId,
		experienceId: 'ai-mate',
		product: productKey,
		touchpointSource: source,
	};

	const { files, clearAttachments, anyFilesUploading } = useConversationFiles({
		assistanceServiceParams,
		key: STORE_KEY,
	});

	const { getChatMode, resetReasoningMode, setWebSearchEnabled } = useChatModeStoreActions();

	const mentionResource = useChatMentionResource({
		productKey,
		siteId,
		orgId,
		currentUserId,
		mentionResourceOverride,
		enableAgents,
		agentSource,
		onAgentSelected,
	});

	const inputExceedsMaxLimit = inputValue.length > MAX_CHARACTERS_INPUT;
	const isEmpty = inputValue.trim().length === 0;

	const isSendDisabled =
		inputExceedsMaxLimit || anyFilesUploading || (isEmpty && !hasMentionOrInlineCard && !hasSkill);

	const handleSubmit = useCallback(async () => {
		const value: DocNode = await actions.getValue();

		if (isSendDisabled) {
			return;
		}

		const {
			deepResearchEnabled,
			thinkDeeperEnabled,
			fastModeEnabled,
			webSearchEnabled,
			appFilters,
		} = getChatMode(STORE_KEY);
		const validFiles = files.filter((file) => !file.error);

		onSubmit({
			prompt: value,
			deepResearchEnabled,
			thinkDeeperEnabled,
			fastModeEnabled,
			webSearchEnabled,
			appFilters: appFilters,
			files: validFiles,
			addCurrentPageAsContextUserPreference: true,
		});

		// Clean up
		actions.clear();
		clearAttachments(STORE_KEY);
		resetReasoningMode(STORE_KEY);
		setWebSearchEnabled(STORE_KEY, false);
	}, [
		actions,
		clearAttachments,
		files,
		getChatMode,
		isSendDisabled,
		onSubmit,
		resetReasoningMode,
		setWebSearchEnabled,
	]);

	useEffect(() => {
		return () => {
			// Reset attachments on unmount
			clearAttachments(STORE_KEY);
		};
	}, [clearAttachments]);

	saveRef.current = handleSubmit;

	const stableOnSave = useCallback(() => saveRef.current?.(), []);

	// Enable People/Agents section split in the @mention typeahead
	const enableAgentSectioning =
		(expValEquals('platform_editor_agent_mentions', 'isEnabled', true) &&
			fg('platform_editor_agent_mentions_drop_one_fixes')) ||
		fg('platform_editor_agent_mention_ask_rovo_jira');
	const { editorApi, preset } = useEditorPreset({
		onSave: stableOnSave,
		arrowKeyPluginConfig: {
			userMessages: [],
		},
		productKey,
		siteId,
		useShortMessages,
		placeholderText,
		enableAgentSectioning,
	});

	const closeTypeAhead = useCallback(() => {
		editorApi?.typeAhead?.actions.close({ insertCurrentQueryAsRawText: false });
	}, [editorApi?.typeAhead?.actions]);

	const openMentionTypeAhead = useCallback(() => {
		closeTypeAhead();
		// Small delay to ensure the editor regains focus after closing the dropdown menu
		// This prevents the typeahead from immediately closing in Safari
		// Original root cause research: https://product-fabric.atlassian.net/browse/AFC-550
		requestAnimationFrame(() => {
			editorApi?.core.actions.focus();
			editorApi?.mention?.actions.openTypeAhead(INPUT_METHOD.QUICK_INSERT);
		});
	}, [closeTypeAhead, editorApi?.core.actions, editorApi?.mention?.actions]);

	const openLinkTypeAhead = useCallback(() => {
		closeTypeAhead();
		const command = editorApi?.hyperlink?.commands.showLinkToolbar(INPUT_METHOD.QUICK_INSERT);
		editorApi?.core.actions.execute(command);
	}, [closeTypeAhead, editorApi?.core.actions, editorApi?.hyperlink?.commands]);

	const openAddTypeAhead = useCallback(() => {
		closeTypeAhead();
		// Small delay to ensure the editor regains focus after closing the dropdown menu
		// This prevents the typeahead from immediately closing in Safari
		// Original root cause research: https://product-fabric.atlassian.net/browse/AFC-550
		requestAnimationFrame(() => {
			editorApi?.core.actions.focus();
			editorApi?.quickInsert?.actions.openTypeAhead(INPUT_METHOD.QUICK_INSERT);
		});
	}, [closeTypeAhead, editorApi?.core.actions, editorApi?.quickInsert?.actions]);

	useEffect(() => {
		if (!!placeholder && !!editorApi) {
			actions.clear();
			actions.focus();
			closeTypeAhead();
			actions.appendText(placeholder.prompt);

			editorApi?.core.actions.execute(({ tr }) => {
				const maxPos = tr.doc.content.size;
				const startPos = Math.min(placeholder.selectionStartIndex, maxPos);
				const endPos = Math.min(placeholder.selectionEndIndex, maxPos);

				const transaction = tr.setSelection(
					TextSelection.between(tr.doc.resolve(startPos), tr.doc.resolve(endPos)),
				);

				return transaction;
			});

			switch (placeholder.type) {
				case 'person':
					openMentionTypeAhead();
					break;
				case 'link':
					openLinkTypeAhead();
					break;
			}

			clearPlaceholder();
		}
	}, [
		editorApi,
		actions,
		clearPlaceholder,
		closeTypeAhead,
		editorApi?.core.actions,
		openLinkTypeAhead,
		openMentionTypeAhead,
		placeholder,
	]);

	useEffect(() => {
		if (!!highlightActionsEditorPlaceholder && !!editorApi) {
			actions.clear();
			actions.focus();
			closeTypeAhead();
			editorApi.core.actions.replaceDocument(highlightActionsEditorPlaceholder);
			editorApi?.core.actions.execute(({ tr }) => {
				const lastNode = tr.doc.lastChild;
				if (lastNode) {
					const lastNodePos = tr.doc.resolve(tr.doc.content.size - lastNode.nodeSize);
					const endPos = tr.doc.resolve(tr.doc.content.size);
					const transaction = tr.setSelection(TextSelection.between(lastNodePos, endPos));

					return transaction;
				}

				return null;
			});
			// This is a workaround to avoid the focus being lost in Jira (and Jira sub-products)
			if (productKey === 'jira' || productKey === 'jsm' || productKey === 'jpd') {
				requestAnimationFrame(() => {
					setTimeout(() => {
						actions.focus();
					}, 1500);
				});
			}
			clearHighlightActionsEditorPlaceholder();
		}
	}, [
		editorApi,
		actions,
		clearHighlightActionsEditorPlaceholder,
		closeTypeAhead,
		highlightActionsEditorPlaceholder,
		productKey,
	]);

	const isConversationAgentic = enableAgents && !!agent;

	// Minimal extension provider that only registers the skill chip node renderer.
	// Passed to the Suspense fallback editor so skill chips in `defaultValue` ADF
	// render immediately without waiting for the skills Relay query to resolve.
	const skillTagFallbackProvider = useSkillTagExtensionProvider(siteId);

	const assistiveLabel = useMemo(
		() =>
			formatMessage(
				useShortMessages ? messages.chatInputAssistiveLabelShort : messages.chatInputAssistiveLabel,
			),
		[formatMessage, useShortMessages],
	);

	const editorProps = useInlineEditorProps({
		assistiveLabel,
		defaultValue,
		mentionResource,
		onPromptContentChange,
		popupsMountPoint,
		preset,
		setHasMentionOrInlineCard,
		setInputValue,
		stableOnSave,
	});

	return (
		<>
			<Stack xcss={cx(styles.stack, files.length === 0 && styles.stackWithoutContexts)}>
				<Box xcss={cx(styles.editorWrapper, compact && styles.editorWrapperCompact)}>
					{showInlineAgentSelector && (
						<AgentSelector
							agent={agent}
							onAgentSelect={onAgentSelect}
							assistanceServiceParams={assistanceServiceParams}
							siteId={siteId}
							shouldRenderInPortal={shouldRenderInPortal}
							compact={compact}
						/>
					)}
					<ErrorBoundary
						fallbackRender={({ error }) => (
							<>
								<ExperienceFailure error={error} name={ExperienceName.LOAD_INLINE_EDITOR} />
								<ExperienceFailure
									error={error}
									name={ExperienceName.LOAD_INLINE_CHAT_PROMPT_INPUT_SKILLS}
								/>
								{/* fallback to editor without skills quick-insert but with chip renderer */}
								<ComposableEditor extensionProviders={skillTagFallbackProvider} {...editorProps} />
							</>
						)}
						onError={(error) => {
							logException({
								exception: error,
								name: ExperienceName.LOAD_INLINE_EDITOR,
								tags: {
									siteId: siteId,
								},
							});
						}}
					>
						<ExperienceStart name={ExperienceName.LOAD_INLINE_EDITOR} />
						<ExperienceStart name={ExperienceName.LOAD_INLINE_CHAT_PROMPT_INPUT_SKILLS} />
						<AtlassianRelayEnvironmentProvider>
							<UFOSegment name="inline-chat-prompt-input-skills">
								<Suspense
									fallback={
										<ComposableEditor
											extensionProviders={skillTagFallbackProvider}
											{...editorProps}
										/>
									}
								>
									<ComposableEditorWithSkills
										editorApi={editorApi}
										isSpecialistPromptInput={isConversationAgentic}
										promptHasSkill={hasSkill}
										enableSkills
										siteId={siteId}
										{...editorProps}
									/>
									<ExperienceSuccess name={ExperienceName.LOAD_INLINE_CHAT_PROMPT_INPUT_SKILLS} />
								</Suspense>
							</UFOSegment>
						</AtlassianRelayEnvironmentProvider>
						<ExperienceSuccess name={ExperienceName.LOAD_INLINE_EDITOR} />
					</ErrorBoundary>
				</Box>
				<InputContextListDisplay attachtmentsStoreKey={STORE_KEY} />
			</Stack>
			<Inline xcss={styles.actions}>
				{showLabsLozenge && (
					<Inline xcss={styles.labsLozenge}>
						<LabsLozenge variant={labsLozengeVariant} />
					</Inline>
				)}
				{(appearance !== 'frameless' || showTrailingActions) && (
					<>
						<Inline alignBlock="center" space="space.050">
							<ChatInputActionButtons
								siteId={siteId}
								attachmentsAndCurrentPageAsContextStoreKey={STORE_KEY}
								isDisabled={false}
								useCurrentPageContext={false}
								onLinkTypeAheadClick={openLinkTypeAhead}
								onMentionTypeAheadClick={openMentionTypeAhead}
								onAddTypeAheadClick={openAddTypeAhead}
								assistanceServiceParams={assistanceServiceParams}
								placement="bottom-end"
								onMenuOpen={closeTypeAhead}
								// Omitted in the `inline` layout so the picker isn't offered in two
								// places at once — the standalone left selector owns it there.
								onAgentSelect={enableAgents && !showInlineAgentSelector ? onAgentSelect : undefined}
								isBrowseAgentsModalOpen={isBrowseAgentsModalOpen}
								setIsBrowseAgentsModalOpen={setIsBrowseAgentsModalOpen}
								shouldRenderInPortal={shouldRenderInPortal}
							/>
						</Inline>

						<IconButton
							isTooltipDisabled={false}
							appearance="primary"
							shape={
								expValEqualsNoExposure('platform_editor_ai_xstate_migration', 'isEnabled', true) &&
								expValEqualsNoExposure(
									'platform_editor_ai_new_aifc_editor_experience',
									'isEnabled',
									true,
								)
									? 'default'
									: 'circle'
							}
							ref={submitButtonRef}
							onClick={() => saveRef.current?.()}
							type="submit"
							isDisabled={isSendDisabled}
							interactionName="ai-mate.send-message-button"
							icon={(iconProps) => <ArrowUpIcon {...iconProps} label={''} />}
							label={formatMessage(messages.sendButtonLabel)}
						/>
					</>
				)}
			</Inline>
		</>
	);
};

export const PromptView = (props: ChatPromptInputProps): JSX.Element => {
	return (
		<EditorContext>
			<WithEditorActions
				render={(actions) => <PromptViewInternal {...props} actions={actions} />}
			/>
		</EditorContext>
	);
};
